import { useRef, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';

import { Section, SectionHeading, Panel, Reveal } from '../components/ui';
import { COMPANY_EMAIL, COMPANY_PHONE, mailHref, phoneHref } from '../data/company';
import { CONTACT_SUBJECTS, TIMELINES } from '../data/services';
import { FONT, RADIUS, TYPE } from '../theme/tokens';

// Valorile sunt publice prin natura lor (URL-ul funcției și cheia de site
// reCAPTCHA ajung oricum în bundle-ul din browser). Secretele — cheia privată
// reCAPTCHA și parola SMTP — stau doar în Secret Manager, lângă funcție.
const ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || '';
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const MESSAGE_MIN = 10;
const MESSAGE_MAX = 5000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Numerele de telefon din construcții vin în toate formele: cu prefix de țară,
// cu paranteze, cu puncte, cu interior. Nu încercăm să le normalizăm — verificăm
// doar că sunt destule cifre cât să fie un număr, și nu prea multe.
const PHONE_RE = /^\+?[\d\s().-]{6,24}$/;
const PHONE_DIGITS = /\d/g;

// Telefonul și termenul sunt opționale: cine vrea doar să scrie două rânduri nu
// trebuie oprit de un câmp în plus. Subiectul e obligatoriu — el decide cine din
// echipă citește mesajul primul.
const EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  subject: '',
  timeline: '',
  message: '',
  website: '',
};

// Codurile întoarse de funcție → cheia de traducere afișată.
const SERVER_ERRORS = {
  captcha_failed: 'contact.errors.captchaFailed',
  captcha_missing: 'contact.errors.captcha',
  rate_limited: 'contact.errors.rate',
  invalid_email: 'contact.errors.email',
  invalid_phone: 'contact.errors.phone',
  invalid_subject: 'contact.errors.subject',
  message_too_short: 'contact.errors.messageShort',
  too_long: 'contact.errors.messageLong',
};

// Câmpurile completate automat.
//
// WebKit repictează fundalul inputului cu culoarea lui deschisă și nu se lasă
// oprit de `background-color` — singura pârghie e o umbră interioară groasă.
// Trei lucruri sunt necesare împreună:
//   1. toate stările — Safari reaplică fundalul la hover și la focus
//   2. `!important` — stilul agentului utilizator are prioritate altfel
//   3. tranziția foarte lungă — Safari repictează fundalul când se stinge orice
//      tranziție pe `background-color`, deci nu o lăsăm să se stingă
// Fără asta, pe iPhone completarea din fișa de contact (calea obișnuită acolo)
// lăsa câmpurile albe cu text alb în schema închisă: ilizibile.
const autofillFix = {
  [[
    '& input:-webkit-autofill',
    '& input:-webkit-autofill:hover',
    '& input:-webkit-autofill:focus',
    '& input:-webkit-autofill:active',
    '& textarea:-webkit-autofill',
    '& textarea:-webkit-autofill:hover',
    '& textarea:-webkit-autofill:focus',
  ].join(', ')]: {
    WebkitBoxShadow: '0 0 0 100px var(--qf-card-2) inset !important',
    WebkitTextFillColor: 'var(--qf-ink) !important',
    caretColor: 'var(--qf-ink)',
    transition: 'background-color 100000s ease-in-out 0s !important',
  },
};

export default function Contact() {
  const { t, i18n } = useTranslation();

  // Widgetul reCAPTCHA are tema lui; o luăm după schema aleasă în bară, nu
  // după cea a sistemului — altfel, cine comută manual pe întuneric rămâne cu
  // o casetă albă lipită în mijlocul formularului.
  const { mode, systemMode } = useColorScheme();
  const isDark = (mode === 'system' ? systemMode : mode) === 'dark';

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [formError, setFormError] = useState(null);
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);

  const configured = Boolean(ENDPOINT && SITE_KEY);
  const lang = i18n.language?.startsWith('ro') ? 'ro' : 'en';

  const setField = (name) => (e) => {
    setForm((f) => ({ ...f, [name]: e.target.value }));
    // Eroarea dispare pe măsură ce omul o repară, nu abia la următorul submit.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: null } : prev));
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = 'contact.errors.required';
    if (!form.lastName.trim()) next.lastName = 'contact.errors.required';

    if (!form.email.trim()) next.email = 'contact.errors.required';
    else if (!EMAIL_RE.test(form.email.trim())) next.email = 'contact.errors.email';

    // Gol e în regulă. Scris greșit, nu: un telefon la care nu se poate suna e
    // mai rău decât niciunul, fiindcă cineva chiar va încerca.
    const phone = form.phone.trim();
    if (phone && (!PHONE_RE.test(phone) || (phone.match(PHONE_DIGITS) || []).length < 7)) {
      next.phone = 'contact.errors.phone';
    }

    if (!form.subject) next.subject = 'contact.errors.required';

    const msg = form.message.trim();
    if (!msg) next.message = 'contact.errors.required';
    else if (msg.length < MESSAGE_MIN) next.message = 'contact.errors.messageShort';
    else if (msg.length > MESSAGE_MAX) next.message = 'contact.errors.messageLong';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const resetCaptcha = () => {
    setCaptcha(null);
    captchaRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;

    setFormError(null);

    if (!configured) {
      setStatus('error');
      setFormError('contact.errors.notConfigured');
      return;
    }

    if (!validate()) {
      setStatus('idle');
      return;
    }

    if (!captcha) {
      setStatus('error');
      setFormError('contact.errors.captcha');
      return;
    }

    setStatus('sending');

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          // Cheile pleacă neschimbate, nu textul de pe ecran: mailul către
          // firmă trebuie să scrie același lucru fie că vizitatorul a completat
          // formularul în română, fie în engleză.
          subject: form.subject,
          timeline: form.timeline,
          message: form.message.trim(),
          // În ce limbă primește vizitatorul confirmarea.
          lang,
          website: form.website, // capcana; un om o lasă goală
          captchaToken: captcha,
        }),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        /* răspuns fără corp JSON */
      }

      if (res.ok && data?.ok) {
        setStatus('sent');
        setForm(EMPTY);
        resetCaptcha();
        return;
      }

      // Un token reCAPTCHA e de unică folosință: după orice eșec trebuie bifat
      // din nou, altfel a doua încercare pică sigur.
      resetCaptcha();
      setStatus('error');
      setFormError(SERVER_ERRORS[data?.error] || 'contact.errors.server');
    } catch {
      resetCaptcha();
      setStatus('error');
      setFormError('contact.errors.network');
    }
  };

  const sending = status === 'sending';

  const directRows = [
    { icon: <MailOutlineRoundedIcon />, label: t('contact.directEmail'), value: COMPANY_EMAIL, href: mailHref },
    { icon: <CallOutlinedIcon />, label: t('contact.directPhone'), value: COMPANY_PHONE, href: phoneHref },
  ];

  return (
    <Section first component="section">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(360px, .95fr)' },
          gap: { xs: 5, md: 8 },
          alignItems: 'start',
        }}
      >
        {/* ────────────────────  Coloana de text  ──────────────────── */}
        <Box sx={{ minWidth: 0 }}>
          <SectionHeading
            component="h1"
            eyebrow={t('contact.eyebrow')}
            title={t('contact.title')}
            lede={t('contact.description')}
            titleSx={TYPE.h1}
            maxWidth="none"
          />

          {/* Calea directă, pentru cine nu vrea formular. */}
          <Reveal delay={0.1} sx={{ mt: { xs: 4.5, md: 6 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {directRows.map((row) => (
                <Box
                  key={row.label}
                  component="a"
                  href={row.href}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    minHeight: 64,
                    px: { xs: 2, sm: 2.5 },
                    borderRadius: `${RADIUS.lg}px`,
                    border: '1px solid var(--qf-line)',
                    backgroundColor: 'var(--qf-card)',
                    textDecoration: 'none',
                    color: 'var(--qf-ink)',
                    transition: 'border-color .22s, background-color .22s',
                    '@media (hover: hover)': {
                      '&:hover': { borderColor: 'var(--qf-line-2)', backgroundColor: 'var(--qf-card-2)' },
                    },
                    '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: 3 },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      flex: 'none',
                      width: 40,
                      height: 40,
                      borderRadius: '12px',
                      backgroundColor: 'var(--qf-accent-soft)',
                      color: 'var(--qf-accent)',
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {row.icon}
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="div"
                      sx={{
                        fontFamily: FONT.mono,
                        fontSize: 11,
                        letterSpacing: '.1em',
                        textTransform: 'uppercase',
                        color: 'var(--qf-ink-3)',
                      }}
                    >
                      {row.label}
                    </Typography>
                    <Typography sx={{ ...TYPE.body, fontWeight: 500, overflowWrap: 'anywhere' }}>
                      {row.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 2.5, color: 'var(--qf-ink-3)' }}>
              <ScheduleRoundedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ ...TYPE.caption }}>{t('contact.responseTime')}</Typography>
            </Box>
          </Reveal>
        </Box>

        {/* ──────────────────────  Formularul  ─────────────────────── */}
        <Reveal delay={0.16}>
          <Panel header={{ title: t('contact.formTitle'), meta: t('contact.formRev') }}>
            <Box sx={{ p: { xs: 2.25, sm: 3 } }}>
              {status === 'sent' ? (
                <Box role="status" sx={{ py: 3, textAlign: 'center' }}>
                  <Box
                    aria-hidden
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 56,
                      height: 56,
                      mx: 'auto',
                      mb: 2.5,
                      borderRadius: '50%',
                      backgroundColor: 'var(--qf-accent-soft)',
                      color: 'var(--qf-accent)',
                    }}
                  >
                    <CheckRoundedIcon sx={{ fontSize: 28 }} />
                  </Box>

                  <Typography component="h2" sx={{ ...TYPE.h3, mb: 1 }}>
                    {t('contact.successTitle')}
                  </Typography>
                  <Typography sx={{ ...TYPE.small, color: 'var(--qf-ink-2)', mb: 3.5 }}>
                    {t('contact.successMessage')}
                  </Typography>

                  <Button onClick={() => setStatus('idle')} variant="outlined">
                    {t('contact.sendAnother')}
                  </Button>
                </Box>
              ) : (
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  noValidate
                  sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2.25,
                    }}
                  >
                    <TextField
                      required
                      label={t('contact.firstName')}
                      name="firstName"
                      autoComplete="given-name"
                      value={form.firstName}
                      onChange={setField('firstName')}
                      error={Boolean(errors.firstName)}
                      helperText={errors.firstName ? t(errors.firstName) : ' '}
                      disabled={sending}
                      fullWidth
                      sx={autofillFix}
                    />
                    <TextField
                      required
                      label={t('contact.lastName')}
                      name="lastName"
                      autoComplete="family-name"
                      value={form.lastName}
                      onChange={setField('lastName')}
                      error={Boolean(errors.lastName)}
                      helperText={errors.lastName ? t(errors.lastName) : ' '}
                      disabled={sending}
                      fullWidth
                      sx={autofillFix}
                    />
                  </Box>

                  {/* Emailul e calea de răspuns; telefonul scurtează drumul
                      când e vorba de o lucrare care începe săptămâna viitoare.
                      Stau împreună fiindcă amândouă răspund la „unde te
                      găsim?". */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2.25,
                      mt: -1.75,
                    }}
                  >
                    <TextField
                      required
                      label={t('contact.email')}
                      name="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={setField('email')}
                      error={Boolean(errors.email)}
                      helperText={errors.email ? t(errors.email) : ' '}
                      disabled={sending}
                      fullWidth
                      sx={autofillFix}
                    />
                    <TextField
                      label={t('contact.phone')}
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={setField('phone')}
                      error={Boolean(errors.phone)}
                      helperText={errors.phone ? t(errors.phone) : ' '}
                      disabled={sending}
                      fullWidth
                      sx={autofillFix}
                    />
                  </Box>

                  {/* Direcția de lucru. Aceleași patru chei ca pe pagina
                      Servicii, plus o ieșire pentru cine nu se regăsește în
                      ele: un mesaj trimis „pe lângă" e mai bun decât unul
                      pierdut fiindcă lista nu-l cuprindea. */}
                  <TextField
                    select
                    required
                    label={t('contact.subject')}
                    name="subject"
                    value={form.subject}
                    onChange={setField('subject')}
                    error={Boolean(errors.subject)}
                    helperText={errors.subject ? t(errors.subject) : ' '}
                    disabled={sending}
                    fullWidth
                    sx={{ mt: -1.75 }}
                  >
                    {CONTACT_SUBJECTS.map((key) => (
                      <MenuItem key={key} value={key}>
                        {key === 'other' ? t('contact.subjectOther') : t(`services.${key}.title`)}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Cât de departe e lucrarea. Rămâne opțional: cine nu știe
                      încă nu trebuie să inventeze o dată ca să poată trimite. */}
                  <TextField
                    select
                    label={t('contact.timeline')}
                    name="timeline"
                    value={form.timeline}
                    onChange={setField('timeline')}
                    helperText=" "
                    disabled={sending}
                    fullWidth
                    sx={{ mt: -1.75 }}
                  >
                    {TIMELINES.map((key) => (
                      <MenuItem key={key} value={key}>
                        {t(`contact.timelines.${key}`)}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    required
                    label={t('contact.message')}
                    name="message"
                    placeholder={t('contact.messagePlaceholder')}
                    multiline
                    minRows={5}
                    value={form.message}
                    onChange={setField('message')}
                    error={Boolean(errors.message)}
                    helperText={errors.message ? t(errors.message) : ' '}
                    disabled={sending}
                    fullWidth
                    sx={{ ...autofillFix, mt: -1.75 }}
                  />

                  {/* Capcana. `tabIndex={-1}` și `aria-hidden` o scot din calea
                      tastaturii și a cititoarelor de ecran; boții completează
                      tot ce găsesc în DOM. */}
                  <Box
                    aria-hidden
                    sx={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}
                  >
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={setField('website')}
                    />
                  </Box>

                  {SITE_KEY && (
                    <Box
                      sx={{
                        mt: -1.75,
                        display: 'flex',
                        justifyContent: { xs: 'center', sm: 'flex-start' },
                      }}
                    >
                      {/* Rama widgetului.
                          Google livrează un iframe de 304×78, dar caseta pe
                          care o desenează înăuntru are 300×74 și e centrată —
                          rămân 2px de fundal de pagină de jur împrejur. Pe temă
                          deschisă nu se vede nimic (alb pe alb); pe temă închisă
                          e un chenar alb aprins în mijlocul formularului.
                          Iframe-ul e de pe alt domeniu, deci nu poate fi
                          stilizat: singura cale e să-l decupăm.

                          Decupăm 3px, nu 2: al treilea taie chenarul de 1px pe
                          care Google îl desenează în jurul casetei, cu culoarea
                          și colțul lui, nu ale noastre. Rămânea singurul câmp
                          din formular cu altă muchie. În locul lui punem aici
                          exact rama celorlalte câmpuri — aceeași linie, același
                          colț, același fundal — deci widgetul se așază în șir
                          cu ele, nu peste ele.

                          Cutia rămâne de 300×74 în total: `box-sizing:
                          border-box` scade cele două linii de 1px, iar
                          conținutul are fix 298×72, cât arătăm din casetă. */}
                      <Box
                        sx={{
                          flex: 'none',
                          width: 300,
                          height: 74,
                          overflow: 'hidden',
                          border: '1px solid var(--qf-line)',
                          borderRadius: `${RADIUS.md}px`,
                          backgroundColor: 'var(--qf-card-2)',
                          // Widgetul are lățime fixă. Coloana formularului are
                          // `lățimea ecranului − 76px` (marginile paginii plus
                          // padding-ul planșei), deci cei 300px încap de la
                          // 376px în sus. Sub pragul ăsta îl micșorăm, în loc
                          // să-l tăiem — și doar acolo, nu pe orice telefon.
                          transformOrigin: 'center',
                          '@media (max-width: 379px)': { transform: 'scale(0.8)' },
                          '& iframe': { display: 'block', margin: '-3px' },
                        }}
                      >
                        <ReCAPTCHA
                          // `hl` și `theme` se citesc o singură dată, la
                          // montare: fără `key`, comutarea RO/EN sau a schemei
                          // de culoare lăsa widgetul în starea veche.
                          key={`${lang}-${isDark ? 'dark' : 'light'}`}
                          ref={captchaRef}
                          sitekey={SITE_KEY}
                          hl={lang}
                          theme={isDark ? 'dark' : 'light'}
                          onChange={(v) => {
                            setCaptcha(v);
                            setFormError(null);
                          }}
                          onExpired={() => setCaptcha(null)}
                          onErrored={() => setCaptcha(null)}
                        />
                      </Box>
                    </Box>
                  )}

                  {formError && (
                    <Typography
                      role="alert"
                      sx={{
                        ...TYPE.small,
                        color: 'var(--qf-danger)',
                        border: '1px solid var(--qf-danger)',
                        backgroundColor: 'var(--qf-danger-soft)',
                        borderRadius: `${RADIUS.md}px`,
                        px: 1.75,
                        py: 1.5,
                      }}
                    >
                      {t(formError)}
                    </Typography>
                  )}

                  <Button type="submit" variant="contained" size="large" disabled={sending} fullWidth>
                    {sending ? (
                      <>
                        <CircularProgress size={18} sx={{ color: 'inherit', mr: 1.5 }} />
                        {t('contact.sending')}
                      </>
                    ) : (
                      t('contact.submit')
                    )}
                  </Button>

                  <Typography sx={{ ...TYPE.caption, color: 'var(--qf-ink-3)', textAlign: 'center' }}>
                    {t('contact.privacy')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Panel>
        </Reveal>
      </Box>
    </Section>
  );
}
